/**
 * App Component
 * Main routing and application entry point for live results
 */

import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { LiveRacePageV2, RunnerDetailPage, MyRunnersPage, RedirectToRaceUrlPage } from './components/v2';
import { SiteFooter } from './components/SiteFooter';
import { StagingBanner } from './components/StagingBanner';

/**
 * 404 Not Found page
 */
function NotFound() {
  return (
    <div className="min-h-screen flex flex-col bg-race-bg">
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-semibold text-race-text mb-2">404</h1>
          <p className="text-race-text-secondary">Page not found</p>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

/**
 * Legacy V1 route redirect (Q8: V1 is retired).
 *
 * V1 lived at /live/:slug and /test/:slug. Rather than 404 those (and break old
 * Slack links), we redirect to the V2 equivalents, preserving the slug:
 *   /live/:slug  → /v2/:slug
 *   /test/:slug  → /v2/test/:slug
 */
function LegacyLiveRedirect({ test = false }: { test?: boolean }) {
  const { slug } = useParams();
  return <Navigate to={test ? `/v2/test/${slug}` : `/v2/${slug}`} replace />;
}

/**
 * Main App component with routing
 */
function App() {
  return (
    <>
      {/* Staging spec §3.2: render before the router so it's the first thing
          on every route when this build is staging; a no-op in production. */}
      <StagingBanner />
      <BrowserRouter>
        <Routes>
          {/* V1 retired (Q8) — redirect old links to their V2 equivalents */}
          <Route path="/live/:slug" element={<LegacyLiveRedirect />} />
          <Route path="/test/:slug" element={<LegacyLiveRedirect test />} />

          {/* V2 Tarawera design routes */}
          <Route path="/v2/:slug" element={<LiveRacePageV2 mode="live" />} />
          <Route path="/v2/test/:slug" element={<LiveRacePageV2 mode="test" />} />
          <Route path="/v2/:slug/runner/:runnerId" element={<RunnerDetailPage />} />
          <Route path="/v2/:slug/my-runners" element={<MyRunnersPage />} />
          <Route
            path="/v2/:slug/livestream"
            element={
              <RedirectToRaceUrlPage
                field="youtube_livestream_url"
                placeholderTitle="Waiting for livestream"
                placeholderBody="The YouTube livestream link will appear here once the race director sets it. Check back on race morning."
              />
            }
          />
          <Route
            path="/v2/:slug/map"
            element={
              <RedirectToRaceUrlPage
                field="spot_tracker_url"
                placeholderTitle="Tracker map not set"
                placeholderBody="The SPOT tracker map link will appear here once the race director sets it."
              />
            }
          />

          {/* Root instructions */}
          <Route
            path="/"
            element={
              <div className="min-h-screen flex flex-col bg-race-bg">
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-md px-6">
                    <h1 className="text-2xl font-semibold text-race-text mb-4">
                      Live Race Results
                    </h1>
                    <p className="text-race-text-secondary">
                      To view live results for a race, navigate to{' '}
                      <code className="bg-gray-200 px-2 py-1 rounded text-sm">
                        /v2/[race-slug]
                      </code>
                    </p>
                  </div>
                </div>
                <SiteFooter />
              </div>
            }
          />

          {/* 404 catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
